/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LA MATRICE `CDC-5` §6.14, EN UN SEUL ENDROIT ET SOUS FORME DE DONNÉES.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `CDC-5` en tête : « **le fait le plus lourd qu'elle hérite** : les neuf domaines clos ont
 * écrit, chacun de son côté, ce qu'un `orgadmin` et le compte qui voit tout ont le droit de
 * faire. **Aucun ne l'a rassemblé.** Sans la matrice, l'implémentation renverra un objet
 * unique avec des conditions à l'intérieur, et **une condition enfouie dans un service ne
 * se relit jamais en revue**. »
 *
 * Ce fichier est la réponse littérale à cette phrase, côté surface. **Une ligne par
 * ressource, deux colonnes de rôle, et rien d'autre** : pas de `if` dans un écran, pas de
 * condition dans un bouton. `ra-core` lit cette table par `authProvider.canAccess`, et
 * **la peau masque d'elle-même** ce qu'un rôle ne peut pas faire — les boutons de la peau
 * consultent `useCanAccess` avant de paraître.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUE CETTE TABLE N'EST PAS, ET IL FAUT LE LIRE AVANT DE S'Y FIER.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * **Ce n'est pas une autorisation.** L'autorisation vit sur les routes, `@Allow()` recopiant
 * la colonne « Rôle requis » du cahier des charges, plus les bornes de périmètre dans les
 * services — dont celle de Q567 sur l'observation, et le filet de cloisonnement derrière.
 * **Une table côté navigateur ne protège rien** : elle décide ce qui s'affiche.
 *
 * Le sens du défaut est donc écrit : **cette table peut être trop stricte sans danger** —
 * un geste caché reste faisable par la route —, et **trop permissive sans danger non plus**
 * — le serveur refuse. Ce qu'elle achète est ailleurs : `CDC-5` F-55 « une absence et un
 * refus se lisent pareil », et un bouton qui mène à un `403` est exactement cette
 * confusion, servie à la personne qui administre.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LES CINQ RESSOURCES ENTIÈREMENT FERMÉES SONT ÉCRITES, PAS OMISES.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `CDC-5` §3 : la ligne de refus de `tutor` et `student` est « écrite en refus explicite
 * **plutôt que déduite du silence** — une absence et un refus se lisent pareil ». La même
 * règle vaut dans cette table : l'espace de fichiers, les conversations, les préférences,
 * les connexions d'un `orgadmin` et les lignes de trace y figurent **avec `false` partout**,
 * là où les omettre aurait produit le même comportement. **Le comportement est le même, la
 * relecture ne l'est pas** : une ligne à `false` se relit contre le cahier des charges, une
 * ligne absente ne se relit pas.
 */

/** Les deux rôles qui atteignent cette surface, et il n'y en a pas d'autre. */
export type RoleAdministrant = 'ORGADMIN' | 'SUPERADMIN';

/**
 * **Les actions, et le vocabulaire est double.**
 *
 * Les cinq premières sont celles que `ra-core` demande de lui-même sur une ressource de
 * CRUD. Les suivantes sont les **gestes métier** de `CDC-5`, que `DOCTRINE.md` §7 nomme la
 * porte de sortie autorisée : « un admin ne fait pas que du CRUD ». Elles passent par le
 * même `canAccess`, donc par la même table — un second mécanisme pour les gestes métier
 * aurait recréé les deux endroits que la matrice existe pour éviter.
 */
export type Action =
  | 'list'
  | 'show'
  | 'create'
  | 'edit'
  | 'delete'
  // les gestes métier de CDC-5, nommés comme le cahier des charges les nomme
  | 'regenerer-le-secret'
  | 'revoquer-le-secret'
  | 'poser-les-colonnes-affichage'
  | 'rattacher'
  | 'retirer-un-rattachement'
  | 'defaire-une-relation'
  | 'creer-une-relation'
  | 'anonymiser'
  | 'creer-un-orgadmin'
  | 'masquer'
  | 'couper'
  | 'observer'
  | 'fabriquer-le-document'
  | 'exporter'
  | 'importer'
  | 'lire-le-reglage-du-parc'
  | 'ecrire-le-reglage-du-parc'
  | 'lire-les-chiffres-reserves';

type Ligne = Partial<Record<Action, readonly RoleAdministrant[]>>;

/** Raccourcis de lecture : la table se relit à côté du tableau de §6.14. */
const LES_DEUX: readonly RoleAdministrant[] = ['ORGADMIN', 'SUPERADMIN'];
const LUI_SEUL: readonly RoleAdministrant[] = ['SUPERADMIN'];
const PERSONNE: readonly RoleAdministrant[] = [];

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LA TABLE. Elle se lit ligne à ligne contre `CDC-5` §6.14.**
 * ═══════════════════════════════════════════════════════════════════════════
 */
export const MATRICE: Readonly<Record<string, Ligne>> = {
  /**
   * **structure** — §6.14 : `orgadmin` lit **la sienne**, ne crée pas, ne modifie pas, ne
   * supprime pas. `superadmin` lit toutes, crée, modifie **son nom interne**.
   *
   * **`delete` est fermé aux DEUX**, et c'est F-56 : « **IL N'EXISTE AUCUN GESTE DE
   * SUPPRESSION D'UNE STRUCTURE** (Q543). Le produit n'a **qu'un seul geste destructif,
   * l'anonymisation d'un compte**, et il ne s'en ajoute pas un second. » Le fournisseur de
   * données lève aussi sur `delete` : deux verrous, parce que celui-ci ne fait que cacher
   * un bouton.
   */
  structures: {
    list: LUI_SEUL, // l'écran d'arrivée du seul rôle qui voit le parc — F-55
    show: LES_DEUX,
    create: LUI_SEUL,
    edit: LUI_SEUL,
    delete: PERSONNE,
    'regenerer-le-secret': LUI_SEUL,
    'revoquer-le-secret': LUI_SEUL,
    'poser-les-colonnes-affichage': LUI_SEUL,
    rattacher: LES_DEUX,
    'retirer-un-rattachement': LES_DEUX,
    exporter: LES_DEUX,
    /**
     * **`importer` EST À `ORGADMIN` SEUL — DÉCISION DU PORTEUR DU 20260824, D-082.**
     *
     * §6.14 l'accordait aux deux : « import des comptes, **par
     * `POST /api/v1/provisionnement/import`** | **oui**, sur **toutes** ». **Le droit est
     * retiré au compte qui voit tout**, et le cahier des charges est amendé en conséquence.
     *
     * **Le constat qui l'a motivé, D-077 :** `@Allow()` nomme bien `SUPERADMIN`, mais le corps
     * de la route **ne porte aucune structure** — elle la déduit de l'appelant, et ce compte
     * « **n'a jamais de rattachement** » (Q535). Le service le dit lui-même en levant : « un
     * import s'applique à une structure. **Le compte qui voit tout n'en désigne aucune par
     * cette route.** »
     *
     * **LES DEUX AUTRES ISSUES ONT ÉTÉ ÉCARTÉES, ET LEURS MOTIFS SONT DIFFÉRENTS.**
     *
     * **Pas un paramètre de structure sur la route de `CDC-1`** : elle est aussi ouverte au
     * secret d'une structure et à un `orgadmin`, pour qui la structure est implicite. Un
     * paramètre facultatif de structure sur une route qu'un secret de structure appelle est
     * **exactement la forme d'un défaut de cloisonnement** — il faudrait le refuser pour eux
     * deux, et c'est cette asymétrie qu'on oublie.
     *
     * **Pas une seconde route rendue à `CDC-5`** non plus, et le plan de test porte l'argument :
     * « sur deux chemins pour un geste, **trois vérifications sur l'un ne disent rien de
     * l'autre**, et c'est exactement la faille que la revue a trouvée — les deux chemins de
     * l'observation en direct **ne déclaraient pas la même autorisation** ». Elle ajoutait une
     * route, trois vérifications, une borne à écrire, le renversement de D-070 — **et elle
     * n'évitait pas le changement de signature du service**, qui est le cœur de la première
     * issue. Q566 aurait été affaiblie sur les quatre autres gestes sans information nouvelle.
     *
     * **CE QUE LE RETRAIT COÛTE, ÉCRIT POUR QU'UN AJOUT LE CONTREDISE VISIBLEMENT.** Plus
     * d'entrée en masse depuis le compte du parc. **La capacité est plus lente, pas absente** :
     * il crée la personne par `POST /provisionnement/comptes`, puis la rattache par
     * `POST /api/v1/admin/structures/:id/rattachements`. Et c'est l'issue que F-60 soutient
     * déjà — « **une structure rattache et détache, elle ne crée pas** ».
     */
    importer: ['ORGADMIN'],
    'creer-un-orgadmin': LUI_SEUL,
  },

  /**
   * **colonnes d'affichage, logo compris** — §6.14, ligne 2 : `orgadmin` **« NON — refus
   * explicite, y compris les siennes »**, champs interdits en réponse : **toutes**.
   * `superadmin` : « **oui, SEUL** », et les vider retire la structure du site public.
   *
   * La ligne existe séparément de `structures` parce que **le refus porte sur des champs
   * d'une ressource dont la lecture est ouverte** : le contrat rend deux objets distincts
   * selon le rôle, `oneOf` sur `role`, et l'écran ne montre ce bloc qu'au rôle qui le
   * reçoit. Sans cette ligne, la seule garde serait « le champ est absent de la réponse »,
   * qui est vraie mais ne se relit pas contre §6.14.
   */
  'colonnes-affichage': {
    show: LUI_SEUL,
    edit: LUI_SEUL,
  },

  /**
   * **cinq réglages de structure** — §6.14 : `orgadmin` lit et modifie **les siens** ;
   * `superadmin` toutes. Aucun `create` : « **sans objet, ils naissent avec la
   * structure** ». Aucun `delete`.
   */
  reglages: {
    show: LES_DEUX,
    edit: LES_DEUX,
  },

  /**
   * **réglage du parc** — §6.14 : `orgadmin` **« NON »** aux quatre colonnes, champs
   * interdits **toutes** ; `superadmin` « **oui, SEUL** ». F-65 critère 3 nomme le refus,
   * `REGLAGE_DE_PARC_INTERDIT`, et D-074 a posé `@CodeDeRefus` pour que la route le porte.
   */
  'reglage-du-parc': {
    'lire-le-reglage-du-parc': LUI_SEUL,
    'ecrire-le-reglage-du-parc': LUI_SEUL,
  },

  /**
   * **secret d'appel** — §6.14 : `orgadmin` **« NON — refus explicite »** partout.
   * `superadmin` le lit « **une seule fois, à l'émission**, et **jamais après** », le
   * régénère et le révoque. **Aucun `show`, pour aucun des deux** : la structure n'en porte
   * que l'**empreinte**, « ce qui rend le réaffichage impossible **par construction** ».
   * Le contrat n'expose donc qu'un booléen dérivé, `secretEmis`.
   */
  secret: {
    show: PERSONNE,
    'regenerer-le-secret': LUI_SEUL,
    'revoquer-le-secret': LUI_SEUL,
  },

  /**
   * **compte** — §6.14 : `orgadmin` lit **les personnes rattachées à sa structure**, ne
   * crée pas, **ne modifie rien** — « ni nom, ni prénom, ni adresse, ni rôle » —, et
   * **n'anonymise personne**. Motif écrit en F-60 : « **UNE STRUCTURE NE TOUCHE JAMAIS UN
   * COMPTE**, elle ne touche que des rattachements », parce qu'« un compte appartient à la
   * personne » et que « changer le rôle d'une personne le changerait **chez toutes ses
   * structures** ».
   *
   * `superadmin` : lit tout le parc, crée, modifie **sauf l'adresse**, anonymise.
   * **L'adresse email est interdite en écriture** — le corps de la route ne porte pas le
   * champ, et un corps qui le porterait reçoit `400`, ce qui vaut mieux que de le voir
   * ignoré.
   */
  comptes: {
    list: LES_DEUX,
    show: LES_DEUX,
    create: LUI_SEUL, // par POST /provisionnement/comptes — D-071
    edit: LUI_SEUL,
    delete: PERSONNE, // un compte ne se supprime pas : il s'anonymise
    anonymiser: LUI_SEUL,
  },

  /**
   * **rattachement** — §6.14 : les deux lisent, les deux **créent**, aucun ne « modifie »
   * — « il se retire et se recrée » —, et les deux **retirent**, « la ligne restant avec sa
   * date de fin ».
   *
   * **`create` reste une route de cette surface**, et l'amendement de D-071 l'a écrit :
   * « `POST /api/v1/admin/structures/:id/rattachements` **reste**, et l'écart avec la route
   * de provisionnement est écrit en F-60 : **une structure rattache et détache, elle ne
   * crée pas**. Provisionner crée-ou-reconnaît une personne ; rattacher prend une personne
   * **qui existe déjà**. »
   */
  rattachements: {
    list: LES_DEUX,
    create: LES_DEUX,
    delete: LES_DEUX, // par DELETE /provisionnement/rattachements/:id — D-071
  },

  /**
   * **relation** — §6.14 : les deux lisent, **relations extérieures comprises**. `orgadmin`
   * défait « **MAIS SEULEMENT celles que sa structure a créées** » ; `superadmin` défait
   * **toutes**.
   *
   * **Cette borne-là ne s'exprime pas dans cette table**, et c'est délibéré : elle dépend
   * de l'objet, pas du rôle. Elle vit dans `provisioning.service.ts` **depuis le LOT-1**,
   * où elle lève `RELATION_NON_CREEE_PAR_CETTE_STRUCTURE` — D-071 : « la borne la plus fine
   * de ce document était **déjà tenue**, et c'est ce qui achève la démonstration ».
   * L'écran affiche donc le geste et **laisse le serveur trancher l'objet**.
   *
   * ═══════════════════════════════════════════════════════════════════════
   * **`creer-une-relation` PASSE DE `LUI_SEUL` À L'`orgadmin` SEUL — AMENDÉ LE 20260826,
   * D-101. C'EST LE SEUL RENVERSEMENT DE CETTE TABLE.**
   * ═══════════════════════════════════════════════════════════════════════
   *
   * **La cellule était accordée au compte qui voit tout, et il ne pouvait pas l'exercer** :
   * F-08 exige qu'« une relation ait toujours un créateur : l'appel d'une structure », et
   * Q535 pose qu'il **n'a jamais de rattachement**. La route refusait donc, à juste titre —
   * D-099. Et la cellule étant fermée à l'`orgadmin`, **aucun rôle humain ne pouvait créer
   * une relation** : le produit ne s'ouvrait à personne sans un appel du CRM.
   *
   * **Ce qui a décidé, et la deuxième raison tranche.** F-08 permet déjà que « l'appel
   * vienne de l'une ou l'autre structure, indifféremment ». **F-09 donne déjà à
   * l'`orgadmin` le geste inverse** — « défaire une relation créée par sa structure » —, et
   * **pouvoir défaire sans pouvoir faire est une asymétrie que rien ne justifie**. Et sa
   * structure se déduit de son rattachement : **aucun paramètre facultatif à borner**, ce
   * qui rendait dangereuse l'issue A de D-077.
   *
   * **`superadmin` est retiré de cette cellule**, et c'est la conséquence mécanique du choix,
   * pas une décision de plus : le garder aurait laissé sur cette route le défaut exact que
   * D-077 nomme. Même disposition que D-082, **retrait de périmètre daté et motivé** — sa
   * clause de réouverture est le jour où un paramètre de structure se décide. **Il garde le
   * geste inverse**, F-09 lui donnant `DELETE` sur toutes les relations.
   *
   * **Et cette table est le SEUL endroit du front qui le sait** : le sens du défaut reste
   * inoffensif des deux côtés — trop stricte, le geste reste faisable par la route ; trop
   * permissive, le serveur refuse.
   */
  relations: {
    list: LES_DEUX,
    // **Ni `LES_DEUX`, ni `LUI_SEUL`** : le seul cas de la table où l'`orgadmin` a un geste
    // que le compte qui voit tout n'a pas. La constante est écrite en clair plutôt que
    // nommée, une cinquième constante pour une cellule n'apprenant rien.
    'creer-une-relation': ['ORGADMIN'],
    'defaire-une-relation': LES_DEUX,
  },

  /**
   * **cours** — §6.14 : `orgadmin` lit **ceux de SES ÉLÈVES**, contenu compris, modifie
   * titre et description « sur les cours portant sa structure », **coupe** un cours en
   * cours, **et ne masque aucun cours**. `superadmin` : tous, et **masquer, lui seul**.
   *
   * **`create` est à `superadmin`** — « oui, **la relation lui restant obligatoire** », et
   * §3 en fait « l'exemple le plus net du fait que l'exception de cloisonnement ne dispense
   * pas de l'autorisation ». Le geste passe par `POST /api/v1/cours`, route de `CDC-2`.
   */
  cours: {
    list: LES_DEUX,
    edit: LES_DEUX, // par PATCH /api/v1/cours/:id — D-071
    create: LUI_SEUL,
    delete: PERSONNE, // un cours ne se supprime pas : il se masque, et lui seul
    masquer: LUI_SEUL,
    couper: LES_DEUX,
    observer: LES_DEUX,
    'fabriquer-le-document': LES_DEUX,
    exporter: LES_DEUX,
  },

  /**
   * **scène d'un tableau** — §6.14 : lecture ouverte aux deux, avec la borne de périmètre ;
   * et **`NON — rien en écriture`** aux deux, sur `edit` comme sur `delete` : « il n'efface
   * aucune scène avant son terme ». C'est la seule ressource de la surface dont la lecture
   * est ouverte et **les deux écritures fermées aux deux rôles**.
   */
  scenes: {
    show: LES_DEUX,
    edit: PERSONNE,
    delete: PERSONNE,
  },

  /**
   * **observation d'un cours en direct** — §6.14 : les deux, `orgadmin` **« sur un cours
   * dont un de ses élèves est présent »** et signalé par un bandeau, `superadmin` **sans
   * être signalé aux présents**. Champs interdits aux deux : **l'IMAGE et le SON,
   * toujours** — refus explicite, et le contrat ne les porte pas.
   *
   * La borne d'objet de Q567 est **sur la route**, pas ici : « une borne d'autorisation qui
   * dépend du chemin emprunté est une faille par construction ». Le refus arrive nommé,
   * `AUCUN_ELEVE_DE_CETTE_STRUCTURE`.
   */
  observations: {
    show: LES_DEUX,
  },

  /**
   * **chiffres d'exploitation** — §6.14 : `orgadmin` lit **les six de l'écran ouvert, sur
   * son périmètre**, et les **six réservés lui sont interdits, « qu'il ne voit nulle
   * part »**. `superadmin` : les six sur le parc **et** par structure, **plus les six
   * réservés**.
   *
   * F-67 nomme le refus, `CHIFFRES_DE_PARC_INTERDITS`, et §7 précise : « un `orgadmin`
   * reçoit `403`, **jamais un objet vide** — une absence et un refus se lisent pareil ».
   */
  chiffres: {
    show: LES_DEUX,
    'lire-les-chiffres-reserves': LUI_SEUL,
  },

  // ═════════════════════════════════════════════════════════════════════════
  // LES CINQ RESSOURCES ENTIÈREMENT FERMÉES — ÉCRITES, PAS OMISES.
  // ═════════════════════════════════════════════════════════════════════════

  /**
   * **espace de fichiers, dossiers, fichiers, partages** — §6.14, « **NON — refus
   * explicite** » aux quatre colonnes pour les deux rôles, champs interdits **tous**. Le
   * `superadmin` la porte en commentaire comme « **seconde ressource entièrement
   * fermée** ». Les routes de `CDC-3` ne l'admettent d'ailleurs pas : `@Allow(TUTOR,
   * STUDENT)`, vérifié au code.
   */
  espace: { list: PERSONNE, show: PERSONNE, create: PERSONNE, edit: PERSONNE, delete: PERSONNE },

  /**
   * **conversation, message, réaction, état de vue, demande de cours, fichier envoyé** —
   * §6.14, « **NON — refus explicite** », et le `superadmin` la porte comme « **première
   * ressource entièrement fermée** ».
   */
  conversations: { list: PERSONNE, show: PERSONNE, create: PERSONNE, edit: PERSONNE, delete: PERSONNE },

  /**
   * **préférences d'une personne, les trois** — §6.14, « **NON — refus explicite** », et
   * le `superadmin` la porte comme « **troisième ressource entièrement fermée** ». F-59
   * l'énumère aussi parmi « ce qu'aucun des deux rôles ne voit d'une personne ».
   */
  preferences: { list: PERSONNE, show: PERSONNE, edit: PERSONNE },

  /**
   * **connexions en cours d'une personne** — **ENTIÈREMENT FERMÉE DEPUIS LE 20260824, D-083.**
   *
   * §6.14 portait `orgadmin` « **NON — refus explicite** » et `superadmin` « **oui**, sans
   * trace, borne acquise ». **Le « oui » est retiré**, décision du porteur, et le cahier des
   * charges est amendé.
   *
   * **Le constat, D-078 :** aucune des vingt-deux routes ne rend les connexions **d'une autre
   * personne**. `GET /api/v1/reglages/connexions` ne prend **aucun paramètre** et rend les
   * siennes — son propre texte le dit, « c'est le seul endroit où vous pouvez les regarder ».
   * Le droit existait au corpus et n'avait aucun chemin, et n'en a jamais eu un : les
   * vingt-sept routes que §7 déclarait avant les amendements du 20260824 n'en portaient pas
   * davantage.
   *
   * **CE QUE LE RETRAIT ACHÈTE, ET C'EST LA SEULE DES SIX CELLULES DANS CE CAS.** La ressource
   * devient **entièrement fermée aux deux rôles**, ce qui la range avec les trois que §6.14
   * nomme déjà ainsi — l'espace de fichiers, les conversations, les préférences. **Le retrait
   * simplifie la matrice au lieu de l'amputer** : il n'y a plus de ligne à moitié ouverte dont
   * la moitié ouverte ne mène nulle part.
   *
   * **Et l'issue écartée n'était pas gratuite** : ouvrir une lecture sur les sessions d'un
   * tiers est une décision de **classification**, §5 rangeant cette donnée au plus haut, et
   * `CDC-4` F-52 en ayant fait la vue d'une personne **sur elle-même** — date, appareil,
   * adresse réseau.
   */
  connexions: { show: PERSONNE, delete: PERSONNE },

  /**
   * **relevé des entrées et des sorties** — §6.14 l'accorde **aux deux rôles** : « les lignes
   * des cours que sa structure porte » pour l'un, « tout le parc » pour l'autre.
   *
   * **LA LIGNE EST À `PERSONNE` PARCE QU'AUCUNE ROUTE NE LE REND ENCORE, ET ELLE VA CHANGER —
   * D-084.** Le porteur a tranché le 20260824 : **on comble**. La route est due, et cette ligne
   * s'ouvre avec elle.
   *
   * **Pourquoi c'était la moins chère des six cellules de D-077 et D-078.**
   * `AttendanceEntry` existe et porte `courseId`, `userId`, `role`, `enteredAt`, `leftAt`,
   * `exitCause` — **et `organizationId`**, c'est-à-dire exactement le périmètre que §6.14
   * réclame. Le filet de cloisonnement la garde donc **sans qu'on écrive une clause**, et
   * combler ne demande ni migration, ni borne nouvelle : une route et un DTO.
   *
   * **Et le retrait aurait coûté une explication que rien ne remplace** : la liste des cours
   * affiche une durée, et ces lignes sont ce qui la compose. Sans elles, une personne qui
   * administre voit un nombre de minutes et **n'a aucun moyen de savoir d'où il vient** — sur
   * la seule surface du produit dont un chiffre sert de base à une facturation.
   *
   * **CETTE LIGNE ÉTAIT LE SEUL `PERSONNE` PROVISOIRE DE CETTE TABLE, ET ELLE NE L'EST PLUS
   * DEPUIS LE 20260830.** Elle disait : « les autres portent un refus du cahier des charges,
   * celle-ci porte une route qui n'est pas encore écrite. **Le jour où elle l'est, `LES_DEUX`
   * remplace `PERSONNE` ici.** » La route est écrite — `GET /api/v1/admin/cours/:id/releve`,
   * carte #18 —, et c'est ce jour-là.
   *
   * **`list` ET `show` PORTENT LA MÊME VALEUR, ET IL N'Y A QU'UN GESTE.** Le relevé n'est pas
   * une ressource qu'on parcourt : il se lit **sur la ligne d'un cours**, et c'est de là que
   * la borne de F-62 lui vient. Les deux actions valent donc ensemble ou pas du tout, et les
   * séparer laisserait croire à une liste de relevés qui n'existe pas.
   *
   * **AUCUNE TRACE, ET C'EST LA SEULE LECTURE DE `admin/` DANS CE CAS AVEC LA PRÉSENCE.**
   * §6.14 réserve la trace à l'ouverture d'une fiche de compte et d'une fiche de structure.
   */
  releves: { list: LES_DEUX, show: LES_DEUX },

  /**
   * **lignes de trace** — §6.14 : `orgadmin` « **NON, jamais, ni sur écran ni en export** ».
   * `superadmin` « oui, toutes, **hors du produit** — **aucun écran de consultation en
   * V1** ». Le refus est donc **le même pour les deux du point de vue de cette surface**,
   * et pour deux motifs différents. F-67 le confirme de l'autre côté : « les lignes ne sont
   * montrées par aucun écran, **seuls leurs comptes le sont** ».
   */
  traces: { list: PERSONNE, show: PERSONNE },
};

/**
 * **La lecture de la table, et son défaut par défaut est le refus.**
 *
 * Une ressource inconnue ou une action non listée rend `false`. C'est le pendant, côté
 * surface, de la garde globale de `DOCTRINE.md` §5 : « **une garde globale refuse tout.**
 * Une route n'est publique que si elle le déclare explicitement. […] Une route ajoutée à la
 * hâte est **fermée par oubli, pas ouverte par oubli**. » Une ressource ajoutée ici sans sa
 * ligne ne s'affiche pas, ce qui se voit tout de suite — l'inverse ne se verrait pas.
 */
export function peut(role: RoleAdministrant, ressource: string, action: string): boolean {
  const ligne = MATRICE[ressource];
  if (ligne === undefined) return false;
  const roles = ligne[action as Action];
  if (roles === undefined) return false;
  return roles.includes(role);
}
